import React, { useRef, useState, useEffect } from 'react';
import { FONT_FAMILY } from '../../constants/typography';

interface DraggableButtonProps {
  nodeType: string;
  label: string;
  iconColor: string;
  nodeWidth: number;
  nodeHeight: number;
  onDragEnd: (nodeType: string, position: { x: number; y: number }) => void;
  onStamp?: (nodeType: string) => void;
  stamped?: boolean;
  stampSticky?: boolean;
}

/** SVG icon for node types - matches the Konva NodeIcon shapes */
const NodeTypeIcon: React.FC<{ type: string; color: string; size?: number }> = ({ type, color, size = 16 }) => {
  const s = size;
  
  switch (type) {
    case 'service':
      // Hexagon
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <polygon points="0,-8 7,-4 7,4 0,8 -7,4 -7,-4" fill={color} />
        </svg>
      );
    case 'database':
    case 'cylinder':
      // Cylinder
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <ellipse cx="0" cy="-4" rx="7" ry="3" fill={color} />
          <rect x="-7" y="-4" width="14" height="10" fill={color} />
          <ellipse cx="0" cy="6" rx="7" ry="3" fill={color} />
        </svg>
      );
    case 'api':
      // Lightning bolt
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <polygon points="2,-8 -4,0 0,0 -2,8 4,0 0,0" fill={color} />
        </svg>
      );
    case 'person':
      // Head + shoulders
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <circle cx="0" cy="-4" r="4" fill={color} />
          <ellipse cx="0" cy="5" rx="7" ry="5" fill={color} />
        </svg>
      );
    case 'queue':
      // Stacked bars
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-7" y="-7" width="14" height="3" rx="1" fill={color} />
          <rect x="-7" y="-2" width="14" height="3" rx="1" fill={color} />
          <rect x="-7" y="3" width="14" height="3" rx="1" fill={color} />
        </svg>
      );
    case 'system':
      // Monitor
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-8" y="-7" width="16" height="10" rx="1" fill={color} />
          <rect x="-2" y="3" width="4" height="3" fill={color} />
          <rect x="-5" y="6" width="10" height="2" rx="1" fill={color} />
        </svg>
      );
    case 'group':
      // Square brackets
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <path d="M-3,-7 L-7,-7 L-7,7 L-3,7" stroke={color} strokeWidth="2" fill="none" />
          <path d="M3,-7 L7,-7 L7,7 L3,7" stroke={color} strokeWidth="2" fill="none" />
        </svg>
      );
    case 'decision':
    case 'diamond':
      // Diamond
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <polygon points="0,-8 8,0 0,8 -8,0" fill={color} />
        </svg>
      );
    case 'external':
      // Dashed rectangle
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-7" y="-6" width="14" height="12" stroke={color} strokeWidth="2" strokeDasharray="3,2" fill="none" />
        </svg>
      );
    case 'waypoint':
      // Pill / reroute
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-8" y="-3" width="16" height="6" rx="3" fill={color} opacity="0.85" />
          <circle cx="-8" cy="0" r="2" fill="white" opacity="0.7" />
          <circle cx="8" cy="0" r="2" fill="white" opacity="0.7" />
        </svg>
      );
    case 'box':
      // Plain rectangle with 4 socket dots (2 each side — bidirectional)
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-7" y="-6" width="14" height="12" rx="2" fill={color} opacity="0.2" stroke={color} strokeWidth="1.5" />
          <circle cx="-7" cy="-2" r="1.5" fill={color} />
          <circle cx="-7" cy="2" r="1.5" fill={color} />
          <circle cx="7" cy="-2" r="1.5" fill={color} />
          <circle cx="7" cy="2" r="1.5" fill={color} />
        </svg>
      );
    case 'text':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <path d="M-6,-6 H6 M0,-6 V7" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </svg>
      );
    case 'note':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <path d="M-6,-7 H4 L7,-4 V7 H-6 Z" fill={color} opacity="0.25" stroke={color} strokeWidth="1.5" />
          <path d="M4,-7 V-4 H7" stroke={color} strokeWidth="1.5" fill="none" />
        </svg>
      );
    case 'arrow':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <path d="M-7,0 H5 M1,-4 L7,0 L1,4" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'line':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <path d="M-7,0 H7" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      );
    case 'oval':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <ellipse cx="0" cy="0" rx="8" ry="5.5" fill={color} opacity="0.25" stroke={color} strokeWidth="1.5" />
        </svg>
      );
    case 'rounded':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-7" y="-5" width="14" height="10" rx="5" fill={color} opacity="0.25" stroke={color} strokeWidth="1.5" />
        </svg>
      );
    case 'hexagon':
    case 'net_lb':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <polygon points="-4,-7 4,-7 8,0 4,7 -4,7 -8,0" fill={color} opacity="0.85" />
        </svg>
      );
    case 'net_cloud':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <ellipse cx="-2" cy="1" rx="5" ry="3.5" fill={color} />
          <ellipse cx="3" cy="0" rx="5" ry="4" fill={color} />
          <ellipse cx="0" cy="-3" rx="4" ry="3" fill={color} />
        </svg>
      );
    case 'net_host':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-6" y="-6" width="12" height="9" rx="1" fill={color} />
          <rect x="-2" y="3" width="4" height="2" fill={color} />
          <rect x="-5" y="5" width="10" height="2" rx="1" fill={color} />
        </svg>
      );
    case 'net_firewall':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-7" y="-6" width="14" height="12" fill={color} opacity="0.3" stroke={color} strokeWidth="1.5" />
          <path d="M-7,-2 H7 M-7,2 H7 M-3,-6 V6 M3,-6 V6" stroke={color} strokeWidth="1" />
        </svg>
      );
    case 'net_router':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <circle cx="0" cy="0" r="6" fill="none" stroke={color} strokeWidth="1.8" />
          <path d="M-4,0 H4 M0,-4 V4" stroke={color} strokeWidth="1.8" />
        </svg>
      );
    case 'uml_class':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <rect x="-7" y="-7" width="14" height="14" fill={color} opacity="0.25" stroke={color} strokeWidth="1.5" />
          <path d="M-7,-2 H7 M-7,3 H7" stroke={color} strokeWidth="1.2" />
        </svg>
      );
    case 'uml_actor':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <circle cx="0" cy="-5" r="2.5" fill={color} />
          <path d="M0,-2 V3 M-4,0 H4 M-3,8 L0,3 L3,8" stroke={color} strokeWidth="1.5" fill="none" />
        </svg>
      );
    case 'pipe_process':
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <polygon points="-8,-5 4,-5 8,0 4,5 -8,5 -4,0" fill={color} opacity="0.85" />
        </svg>
      );
    default:
      return (
        <svg width={s} height={s} viewBox="-10 -10 20 20">
          <polygon points="0,-8 7,-4 7,4 0,8 -7,4 -7,-4" fill={color} />
        </svg>
      );
  }
};

export const DraggableButton: React.FC<DraggableButtonProps> = ({
  nodeType,
  label,
  iconColor,
  nodeWidth,
  nodeHeight,
  onDragEnd,
  onStamp,
  stamped = false,
  stampSticky = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const startPos = useRef({ x: 0, y: 0 });

  // Create/update drag ghost
  const getOrCreateGhost = () => {
    let ghost = document.getElementById('drag-ghost') as HTMLDivElement | null;
    if (!ghost) {
      ghost = document.createElement('div');
      ghost.id = 'drag-ghost';

      if (nodeType === 'waypoint') {
        Object.assign(ghost.style, {
          position: 'fixed',
          zIndex: '9999',
          pointerEvents: 'none',
          width: '40px',
          height: '16px',
          backgroundColor: '#1e293b',
          border: `1px solid ${iconColor}80`,
          borderRadius: '8px',
          opacity: '0.9',
          boxShadow: `0 4px 16px rgba(0,0,0,0.5)`,
          transition: 'none',
        });
      } else {
        Object.assign(ghost.style, {
          position: 'fixed',
          zIndex: '9999',
          pointerEvents: 'none',
          width: `${nodeWidth}px`,
          height: `${nodeHeight}px`,
          backgroundColor: '#161c2d',
          border: `2px solid ${iconColor}`,
          borderLeft: `5px solid ${iconColor}`,
          borderRadius: '10px',
          opacity: '0.85',
          boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${iconColor}30`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 14px',
          transition: 'none',
        });

        const typeEl = document.createElement('div');
        typeEl.style.cssText = `font-size:9px;font-weight:700;color:${iconColor};text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px;font-family:${FONT_FAMILY};`;
        typeEl.textContent = nodeType;

        const labelEl = document.createElement('div');
        labelEl.style.cssText = `font-size:13px;font-weight:600;color:#dde3f0;font-family:${FONT_FAMILY};`;
        labelEl.textContent = label;

        ghost.appendChild(typeEl);
        ghost.appendChild(labelEl);
      }

      document.body.appendChild(ghost);
    }
    return ghost;
  };

  const removeGhost = () => {
    const ghost = document.getElementById('drag-ghost');
    if (ghost) document.body.removeChild(ghost);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
    setHasMoved(false);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      if (!hasMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        setHasMoved(true);
      }

      const ghost = getOrCreateGhost();
      // Center ghost on cursor
      ghost.style.left = `${e.clientX - nodeWidth / 2}px`;
      ghost.style.top  = `${e.clientY - nodeHeight / 2}px`;
    };

    const onMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      removeGhost();

      if (!hasMoved) return;

      // data-canvas-area fills the full viewport, so its rect is (0,0)
      // Stage coords === screen coords — place node centered on cursor
      const canvasEl = document.querySelector('[data-canvas-area]');
      if (!canvasEl) return;
      const rect = canvasEl.getBoundingClientRect();

      const dropX = e.clientX - rect.left;
      const dropY = e.clientY - rect.top;

      if (dropX >= 0 && dropY >= 0 && dropX <= rect.width && dropY <= rect.height) {
        // Center the node on where the cursor dropped
        onDragEnd(nodeType, {
          x: dropX - nodeWidth  / 2,
          y: dropY - nodeHeight / 2,
        });
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, hasMoved, nodeType, nodeWidth, nodeHeight, onDragEnd]);

  // Cleanup ghost on unmount
  useEffect(() => () => removeGhost(), []);

  const handleClick = () => {
    if (hasMoved) return;
    if (onStamp) {
      onStamp(nodeType);
      return;
    }
  };

  return (
    <button
      ref={buttonRef}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className="group flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md transition-all duration-150 text-left"
      title={onStamp
        ? `Click to stamp ${label} once, click again to keep drawing, or drag onto the canvas`
        : `Drag ${label} onto the canvas`}
      style={{
        background: stamped || (isDragging && hasMoved) ? `${iconColor}18` : 'rgba(255,255,255,0.03)',
        border: `1px ${stamped && !stampSticky ? 'dashed' : 'solid'} ${stamped || (isDragging && hasMoved) ? iconColor + '70' : 'rgba(255,255,255,0.06)'}`,
        cursor: isDragging ? 'grabbing' : onStamp ? 'crosshair' : 'grab',
        userSelect: 'none',
        width: '100%',
      }}
    >
      <span
        className="flex h-4 w-4 flex-shrink-0 items-center justify-center [&>svg]:block transition-transform duration-150 group-hover:scale-110"
        style={{ filter: `drop-shadow(0 0 3px ${iconColor}50)` }}
      >
        <NodeTypeIcon type={nodeType} color={iconColor} size={14} />
      </span>
      <span
        className="min-w-0 flex-1 leading-tight"
        style={{
          color: '#c8d0e0',
          fontSize: 11,
          fontWeight: 500,
          fontFamily: FONT_FAMILY,
          whiteSpace: 'normal',
          overflowWrap: 'normal',
          wordBreak: 'normal',
        }}
      >{label}</span>
    </button>
  );
};
